import React, { useCallback, useEffect, useMemo } from 'react';
import {
  NodeProps,
  Handle,
  Position,
  useReactFlow,
  useUpdateNodeInternals,
} from '@xyflow/react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Zap,
  Mail,
  Timer,
  ClipboardList,
  GitBranch,
  PlusIcon,
  Link2,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { selectSegments } from 'store/segments';
import {
  hasTriggerConditions,
  formatTriggerConditionsForNode,
  formatEventTriggerConditionsForNode,
} from '../../utils/workflowConditionHelpers';
import { formatDelayForNode } from '../../utils/workflowNodeHelpers';
import { getEmailTemplateRequest } from '../../store/emailTemplates/emailTemplates.action';
import {
  selectEmailTemplateById,
  selectEmailTemplateLoadingById,
} from '../../store/emailTemplates/emailTemplates.selector';
import {
  getUnsubscribeGroupsRequest,
  selectUnsubscribeGroups,
} from '../../store/unsubscribeGroups';
import TriggerNode from './nodes/trigger/TriggerNode';
import DelayNode from './nodes/delay/DelayNode';
import EmailNode from './nodes/email/EmailNode';

interface WorkflowNodeData {
  label: string;
  config?: any;
  status?: 'draft' | 'configured' | 'error';
  type?: string;
  isFixed?: boolean;
  isDefault?: boolean;
  isTerminal?: boolean;
  activeAddBranch?: 'true' | 'false' | null;
  isAddTarget?: boolean;
  trueBranchTerminal?: boolean;
  falseBranchTerminal?: boolean;
  /** True when both branches from a condition merge into this node — split top handles */
  mergeIncomingBranches?: boolean;
  isDragMode?: boolean;
  isNodeDragMode?: boolean;
  isDraggingNode?: boolean;
  shouldShowAddAffordance?: boolean;
  shouldCreateSkeletonGap?: boolean;
  skeletonGapDirection?: 'up' | 'down' | null;
}

const NODE_TYPE_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  trigger: Zap,
  email: Mail,
  'time-delay': Timer,
  'true-false': GitBranch,
  webhook: Link2,
};

const NODE_TYPE_LABELS: Record<string, string> = {
  trigger: 'Trigger',
  email: 'Action',
  'time-delay': 'Delay',
  'true-false': 'Condition Logic',
  webhook: 'Integration',
};

const WorkflowNode = ({ data, selected, id }: NodeProps) => {
  const nodeData = data as unknown as WorkflowNodeData;
  const { setNodes } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();

  useEffect(() => {
    updateNodeInternals(id);
  }, [id, updateNodeInternals, nodeData.mergeIncomingBranches]);
  const segments = useSelector(selectSegments);
  const dispatch = useDispatch();
  const { id: campaignId } = useParams<{ id: string }>();
  const unsubscribeGroups = useSelector(selectUnsubscribeGroups);

  const templateId =
    nodeData.type === 'email' ? nodeData.config?.templateId : null;

  const emailTemplateSelector = useMemo(
    () => (templateId ? selectEmailTemplateById(templateId) : () => null),
    [templateId]
  );
  const emailTemplateLoadingSelector = useMemo(
    () =>
      templateId ? selectEmailTemplateLoadingById(templateId) : () => false,
    [templateId]
  );

  const emailTemplate = useSelector(emailTemplateSelector);
  const emailTemplateLoading = useSelector(emailTemplateLoadingSelector);

  const sendingBehavior =
    nodeData.type === 'email' ? nodeData.config?.sendingBehavior : null;
  const subscriptionPreference =
    nodeData.type === 'email' ? nodeData.config?.subscriptionPreference : null;
  const messageLimit =
    nodeData.type === 'email' ? nodeData.config?.messageLimit : null;

  useEffect(() => {
    if (templateId && nodeData.type === 'email') {
      if (!emailTemplate) {
        dispatch(getEmailTemplateRequest(templateId));
      }
      dispatch(getUnsubscribeGroupsRequest());
    }
  }, [templateId, nodeData.type, dispatch, emailTemplate]);

  const contentTo = campaignId
    ? `/campaigns/${campaignId}/node/${id}/email-builder${templateId ? `/${templateId}` : ''}`
    : undefined;

  const nodeConditions = useMemo(
    () => nodeData.config?.conditions || {},
    [nodeData.config]
  );

  const triggerHasConditions = useMemo(() => {
    if (nodeData.type !== 'trigger') return true;
    return hasTriggerConditions(nodeConditions);
  }, [nodeData.type, nodeConditions]);

  const formattedTriggerConditions = useMemo(() => {
    if (nodeData.type !== 'trigger') return '';
    const isEventTrigger = nodeData.config?.trigger === 'event';
    return isEventTrigger
      ? formatEventTriggerConditionsForNode(nodeConditions)
      : formatTriggerConditionsForNode(nodeConditions, segments);
  }, [nodeData.type, nodeData.config, nodeConditions, segments]);

  const formattedDelay = useMemo(() => {
    return formatDelayForNode(nodeData.config?.delay);
  }, [nodeData.config]);

  const nodeHasConditions = useMemo(() => {
    return hasTriggerConditions(nodeConditions);
  }, [nodeConditions]);

  const formattedNodeConditions = useMemo(() => {
    return formatTriggerConditionsForNode(nodeConditions, segments);
  }, [nodeConditions, segments]);

  const NodeTypeIcon = useMemo(
    () => NODE_TYPE_ICONS[nodeData.type || ''] || ClipboardList,
    [nodeData.type]
  );

  const handleChooseTriggerClick = useCallback(() => {
    setNodes((nodes) =>
      nodes.map((node) => ({
        ...node,
        selected: node.id === id,
      }))
    );
  }, [id, setNodes]);

  const handleAddNodeClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, branch?: 'true' | 'false') => {
      e.stopPropagation();
      const event = new CustomEvent('addNodeAfterNode', {
        detail: { nodeId: id, branch: branch || null },
      });
      window.dispatchEvent(event);
    },
    [id]
  );

  if (nodeData.type === 'merge') {
    return (
      <div className="relative w-3 h-3 rounded-full bg-primary/70 border border-card shadow-sm">
        <Handle
          type="target"
          position={Position.Top}
          className="!w-0 !h-0 !bg-transparent !border-0 !rounded-none !opacity-0 pointer-events-none"
        />
        <Handle
          type="source"
          position={Position.Bottom}
          className="!w-0 !h-0 !bg-transparent !border-0 !rounded-none !opacity-0 pointer-events-none"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'group relative bg-card border-2 rounded-lg shadow-sm w-[300px] min-h-0 transition-all duration-150',
        'border-border',
        selected && 'border-primary shadow-md',
        nodeData.status === 'error' && 'border-destructive',
        nodeData.status === 'configured' && 'border-success',
        (nodeData.isFixed || nodeData.isDefault) && 'border-primary',
        nodeData.isDragMode && 'opacity-80 scale-[0.99] saturate-75',
        selected &&
          nodeData.isDragMode &&
          'opacity-100 scale-[1.02] saturate-100',
        nodeData.isNodeDragMode &&
          !nodeData.isDraggingNode &&
          'opacity-60 saturate-75',
        nodeData.isDraggingNode &&
          'scale-[1.04] shadow-xl ring-2 ring-primary/30 z-20',
        nodeData.shouldCreateSkeletonGap &&
          nodeData.skeletonGapDirection === 'up' &&
          '-translate-y-[50px]',
        nodeData.shouldCreateSkeletonGap &&
          nodeData.skeletonGapDirection === 'down' &&
          'translate-y-[50px]'
      )}
    >
      {nodeData.type !== 'trigger' &&
        (nodeData.mergeIncomingBranches ? (
          <>
            <Handle
              id="merge-true"
              type="target"
              position={Position.Top}
              className="!w-3 !h-3 !bg-primary !border-2 !border-card !rounded-full"
              style={{ left: '28%' }}
            />
            <Handle
              id="merge-false"
              type="target"
              position={Position.Top}
              className="!w-3 !h-3 !bg-muted-foreground !border-2 !border-card !rounded-full"
              style={{ left: '72%' }}
            />
          </>
        ) : (
          <Handle
            type="target"
            position={Position.Top}
            className="!w-3 !h-3 !bg-primary !border-2 !border-card !rounded-full"
          />
        ))}

      <div className="flex flex-col space-y-3">
        <div
          className="rounded-t-md overflow-hidden min-h-[52px] flex items-center gap-3 px-4 py-3"
          style={{
            background:
              'linear-gradient(135deg,rgb(88, 89, 156) 0%, hsl(var(--primary-hover)) 50%, hsl(var(--primary-active)) 100%)',
          }}
        >
          <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center text-primary-foreground">
            <NodeTypeIcon className="w-4 h-4" aria-hidden />
          </div>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-medium text-primary-foreground/90 uppercase tracking-wider">
                {NODE_TYPE_LABELS[nodeData.type || ''] || 'Step'}
              </p>
              <p className="text-sm font-semibold text-primary-foreground truncate">
                {nodeData.label}
              </p>
            </div>
            {nodeData.isDefault && (
              <div className="w-2 h-2 bg-primary-foreground/80 rounded-full flex-shrink-0" />
            )}
          </div>
        </div>

        {nodeData.type === 'trigger' && (
          <TriggerNode
            triggerType={
              nodeData.config?.trigger === 'event'
                ? 'event'
                : 'attribute-segment'
            }
            event={nodeData.config?.event ?? ''}
            hasConditions={triggerHasConditions}
            formattedConditions={formattedTriggerConditions}
            onChooseTrigger={handleChooseTriggerClick}
          />
        )}
        {nodeData.type === 'email' && (
          <EmailNode
            templateId={templateId}
            emailTemplate={emailTemplate}
            emailTemplateLoading={emailTemplateLoading}
            sendingBehavior={sendingBehavior}
            subscriptionPreference={subscriptionPreference}
            messageLimit={messageLimit}
            unsubscribeGroups={unsubscribeGroups}
            contentTo={contentTo}
          />
        )}
        {nodeData.type === 'time-delay' && (
          <DelayNode
            formattedDelay={formattedDelay}
            formattedConditions={formattedNodeConditions}
            hasConditions={nodeHasConditions}
          />
        )}
        {nodeData.type === 'true-false' && (
          <div className="px-4 pb-4 space-y-3">
            <div className="rounded-md border border-border bg-background px-3 py-2">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Conditions
              </p>
              <p className="text-xs text-foreground line-clamp-3">
                {nodeHasConditions
                  ? formattedNodeConditions
                  : 'No conditions configured yet.'}
              </p>
            </div>
          </div>
        )}
        {nodeData.type === 'webhook' && (
          <div className="px-4 pb-4 space-y-3">
            <div className="rounded-md border border-border bg-background px-3 py-2">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Request
              </p>
              <p className="text-xs text-foreground line-clamp-2">
                {(nodeData.config?.method || 'POST') +
                  ' ' +
                  (nodeData.config?.webhookUrl || 'No URL configured')}
              </p>
            </div>
          </div>
        )}
        {nodeData.type !== 'trigger' &&
          nodeData.type !== 'email' &&
          nodeData.type !== 'time-delay' &&
          nodeData.type !== 'true-false' &&
          nodeHasConditions && (
            <div className="mt-1 space-y-1">
              <p className="text-xs text-muted-foreground line-clamp-2">
                {formattedNodeConditions}
              </p>
            </div>
          )}
      </div>

      {nodeData.type === 'true-false' ? (
        <>
          <Handle
            id="true"
            type="source"
            position={Position.Bottom}
            className="!w-3 !h-3 !bg-primary !border-2 !border-card !rounded-full"
            style={{ left: '28%' }}
          />
          <Handle
            id="false"
            type="source"
            position={Position.Bottom}
            className="!w-3 !h-3 !bg-muted-foreground !border-2 !border-card !rounded-full"
            style={{ left: '72%' }}
          />
        </>
      ) : nodeData.type !== 'exit' ? (
        <Handle
          type="source"
          position={Position.Bottom}
          className="!w-3 !h-3 !bg-primary !border-2 !border-card !rounded-full"
        />
      ) : null}

      {nodeData.type === 'true-false' ? (
        <div className="absolute left-1/2 -bottom-24 -translate-x-1/2 w-[260px]">
          <div className="mt-4 flex justify-between px-6">
            {nodeData.trueBranchTerminal ? (
              <div className="flex flex-col items-center gap-1">
                <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  True
                </span>
                <button
                  type="button"
                  onClick={(e) => handleAddNodeClick(e, 'true')}
                  className={cn(
                    'relative w-10 h-10 rounded-full border-2 border-dashed bg-card transition-colors flex items-center justify-center shadow-sm',
                    !nodeData.shouldShowAddAffordance &&
                      'opacity-0 pointer-events-none',
                    nodeData.activeAddBranch === 'true'
                      ? 'border-primary text-primary ring-2 ring-primary/30 ring-offset-2'
                      : 'border-primary/40 text-primary/70 hover:border-primary hover:text-primary group-hover:opacity-100'
                  )}
                  title="Add true branch node"
                >
                  {nodeData.activeAddBranch === 'true' && (
                    <>
                      <span
                        className="absolute inset-0 rounded-full bg-primary/25 animate-ping"
                        style={{ animationDuration: '1.2s' }}
                      />
                      <span
                        className="absolute inset-0 rounded-full bg-primary/15 animate-ping"
                        style={{
                          animationDuration: '1.8s',
                          animationDelay: '150ms',
                        }}
                      />
                    </>
                  )}
                  <PlusIcon
                    className={cn(
                      'relative z-10 w-4 h-4 transition-transform duration-200',
                      nodeData.activeAddBranch === 'true' &&
                        'scale-110 rotate-90'
                    )}
                  />
                </button>
              </div>
            ) : (
              <div className="w-10 h-10" />
            )}
            {nodeData.falseBranchTerminal ? (
              <div className="flex flex-col items-center gap-1">
                <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  False
                </span>
                <button
                  type="button"
                  onClick={(e) => handleAddNodeClick(e, 'false')}
                  className={cn(
                    'relative w-10 h-10 rounded-full border-2 border-dashed bg-card transition-colors flex items-center justify-center shadow-sm',
                    !nodeData.shouldShowAddAffordance &&
                      'opacity-0 pointer-events-none',
                    nodeData.activeAddBranch === 'false'
                      ? 'border-primary text-primary ring-2 ring-primary/30 ring-offset-2'
                      : 'border-primary/40 text-primary/70 hover:border-primary hover:text-primary group-hover:opacity-100'
                  )}
                  title="Add false branch node"
                >
                  {nodeData.activeAddBranch === 'false' && (
                    <>
                      <span
                        className="absolute inset-0 rounded-full bg-primary/25 animate-ping"
                        style={{ animationDuration: '1.2s' }}
                      />
                      <span
                        className="absolute inset-0 rounded-full bg-primary/15 animate-ping"
                        style={{
                          animationDuration: '1.8s',
                          animationDelay: '150ms',
                        }}
                      />
                    </>
                  )}
                  <PlusIcon
                    className={cn(
                      'relative z-10 w-4 h-4 transition-transform duration-200',
                      nodeData.activeAddBranch === 'false' &&
                        'scale-110 rotate-90'
                    )}
                  />
                </button>
              </div>
            ) : (
              <div className="w-10 h-10" />
            )}
          </div>
        </div>
      ) : (
        nodeData.type !== 'exit' &&
        nodeData.isTerminal && (
          <button
            type="button"
            onClick={(e) => handleAddNodeClick(e)}
            className={cn(
              'relative absolute left-1/2 -bottom-16 -translate-x-1/2 w-9 h-9 rounded-full border-2 border-dashed bg-card transition-colors flex items-center justify-center shadow-sm',
              !nodeData.shouldShowAddAffordance &&
                'opacity-0 pointer-events-none',
              nodeData.isAddTarget
                ? 'border-primary text-primary'
                : 'border-primary/40 text-primary/70 hover:border-primary hover:text-primary group-hover:opacity-100'
            )}
            title="Add next node"
          >
            {nodeData.isAddTarget && (
              <>
                <span
                  className="absolute inset-0 rounded-full bg-primary/25 animate-ping"
                  style={{ animationDuration: '1.2s' }}
                />
                <span
                  className="absolute inset-0 rounded-full bg-primary/15 animate-ping"
                  style={{
                    animationDuration: '1.8s',
                    animationDelay: '150ms',
                  }}
                />
              </>
            )}
            <PlusIcon
              className={cn(
                'relative z-10 w-3.5 h-3.5 transition-transform duration-200',
                nodeData.isAddTarget && 'scale-110 rotate-90'
              )}
            />
          </button>
        )
      )}
    </div>
  );
};

export default React.memo(WorkflowNode);
